<template>
  <section class="lotto-round-report hdg-report-theme">
    <b-card
      no-body
      class="report-context-card"
    >
      <div class="report-context-header">
        <b-button
          variant="flat-secondary"
          class="back-button"
          :aria-label="$t('กลับไปหน้าผลหวย')"
          @click="goBackToResults"
        >
          <feather-icon
            icon="ArrowLeftIcon"
            size="20"
          />
        </b-button>

        <div class="report-title-wrap">
          <span
            v-if="countryCode"
            class="flag-icon flag-icon-rounded report-country-flag"
            :class="`flag-icon-${countryCode}`"
          />
          <h3 class="report-title mb-0 text-truncate">
            {{ huayName }}
          </h3>
          <feather-icon
            v-if="roundDate"
            icon="CalendarIcon"
            size="14"
            class="report-calendar-icon"
          />
          <div class="report-round-meta">
            <span v-if="roundDate">
              {{ roundDate }}
            </span>
            <span class="round-id">
              {{ $t('รอบ') }}: {{ roundId }}
            </span>
          </div>
          <div
            v-if="canManageReportBuild"
            class="report-round-action"
          >
            <b-button
              variant="flat-secondary"
              size="sm"
              class="report-rebuild-button"
              :disabled="!canRebuildReport"
              @click="requestReportBuild"
            >
              <feather-icon
                icon="RefreshCwIcon"
                size="14"
              />
              <span>{{ $t('คำนวณรายงานรอบนี้ใหม่') }}</span>
            </b-button>
          </div>
        </div>

        <div
          class="report-scope-controls"
          :class="{ 'has-agent-selector': isMaster }"
        >
          <div
            v-if="isMaster"
            class="agent-selector-wrap"
          >
            <AgentSelector
              v-model="selectedAgent"
              :auto-select-default="false"
              :emit-self-as-option="true"
              :placeholder="$t('เลือกตัวแทน')"
              size="sm"
              @change="onAgentChanged"
            />
          </div>
          <div class="include-downlines-control">
            <span>{{ $t('รวมตัวแทนย่อย') }}</span>
            <b-form-checkbox
              v-model="includeDownlines"
              switch
              class="include-downlines-switch"
              :aria-label="$t('รวมตัวแทนย่อย')"
              @change="onScopeChanged"
            />
          </div>
        </div>
      </div>
    </b-card>

    <b-card
      no-body
      class="report-content-card"
    >
      <div
        class="report-tabs"
        role="tablist"
        :aria-label="$t('รูปแบบรายงาน')"
      >
        <button
          v-for="tab in tabs"
          :key="tab.key"
          type="button"
          role="tab"
          class="report-tab"
          :class="{ active: activeView === tab.key }"
          :aria-selected="activeView === tab.key ? 'true' : 'false'"
          @click="selectView(tab.key)"
        >
          <span :class="['tab-icon', `tab-icon-${tab.variant}`]">
            <feather-icon
              :icon="tab.icon"
              size="18"
            />
          </span>
          <span>{{ tab.label }}</span>
        </button>
      </div>

      <div class="report-body">
        <div
          class="report-height-viewport"
          :class="{ 'report-height-viewport--static': !animateControlsHeight }"
          :style="animateControlsHeight && controlsHeight !== null ? { height: `${controlsHeight}px` } : null"
        >
          <div
            ref="reportControls"
            class="report-height-content"
          >
            <div class="report-status-row">
              <div>
                <h4 class="mb-25">
                  {{ activeTab.label }}
                </h4>
                <small class="text-muted">
                  {{ activeTab.description }}
                </small>
              </div>
              <div class="report-metadata">
                <span>{{ summaryScopeLabel }}</span>
                <span>
                  {{ $t('ข้อมูล ณ') }}
                  <time
                    v-if="!loading && response.generatedAt"
                    :datetime="response.generatedAt"
                  >{{ formatDate(response.generatedAt) }}</time>
                  <span v-else>—</span>
                </span>
              </div>
            </div>

            <b-alert
              :show="showReportBuildBanner"
              :variant="buildProblem ? 'warning' : 'info'"
              class="mb-1"
            >
              <div
                class="p-1"
                role="status"
                aria-live="polite"
              >
                <strong>{{ buildBannerTitle }}</strong>
                <div v-if="showBuildProgress && !buildActive && !buildSubmitting && !buildStatusError && !buildRequestError">
                  {{ buildDescription }}
                </div>
                <div v-if="buildActive || buildSubmitting">
                  {{ $t('กำลังแสดงข้อมูลเดิม รายงานจะอัปเดตอัตโนมัติเมื่อคำนวณเสร็จ') }}
                </div>
                <small
                  v-if="buildStatus && buildStatus.updatedAt"
                  class="d-block mt-25"
                >
                  {{ $t('อัปเดตสถานะล่าสุด') }} {{ formatDate(buildStatus.updatedAt) }}
                </small>
              </div>
            </b-alert>

            <b-alert
              :show="response.isComplete === false && !showReportBuildBanner"
              variant="warning"
              class="mb-1"
            >
              <div class="d-flex align-items-start">
                <feather-icon
                  icon="ClockIcon"
                  size="18"
                  class="mr-75 mt-25"
                />
                <div>
                  <strong class="d-block">{{ $t('ข้อมูลรอบนี้กำลังประมวลผล') }}</strong>
                  <small>{{ $t('ตัวเลขที่แสดงอาจยังไม่ครบ ระบบจะอัปเดตเมื่อประมวลผลเสร็จ') }}</small>
                </div>
              </div>
            </b-alert>

            <b-row
              class="summary-grid"
              :aria-busy="loading ? 'true' : 'false'"
            >
              <b-col
                v-for="card in summaryCards"
                :key="card.key"
                cols="6"
                xl="3"
                class="mb-1"
              >
                <div class="summary-card">
                  <div :class="['summary-icon', 'report-icon-frame', `bg-light-${card.variant}`, `text-${card.variant}`]">
                    <feather-icon
                      :icon="card.icon"
                      size="18"
                    />
                  </div>
                  <div class="summary-content min-width-0">
                    <small class="text-muted d-block text-truncate">{{ card.label }}</small>
                    <strong :class="['summary-value', showSummary ? card.valueClass : null]">
                      {{ showSummary ? card.value : '—' }}
                    </strong>
                  </div>
                </div>
              </b-col>
            </b-row>

            <div
              v-if="activeView !== 'type'"
              class="report-filter-panel"
            >
              <b-row class="align-items-end">
                <b-col
                  v-if="activeView === 'number' || activeView === 'member'"
                  cols="12"
                  md="6"
                  lg="4"
                >
                  <b-form-group
                    :label="searchLabel"
                    label-for="lotto-report-search"
                  >
                    <b-input-group>
                      <b-input-group-prepend is-text>
                        <feather-icon
                          icon="SearchIcon"
                          size="15"
                        />
                      </b-input-group-prepend>
                      <b-form-input
                        id="lotto-report-search"
                        v-model.trim="filters.search"
                        type="search"
                        :maxlength="activeView === 'number' ? 6 : 64"
                        :placeholder="searchPlaceholder"
                        @keydown.enter="applyFilters"
                      />
                    </b-input-group>
                  </b-form-group>
                </b-col>

                <b-col
                  v-if="activeView === 'number' || activeView === 'items'"
                  cols="12"
                  sm="6"
                  md="3"
                  lg="2"
                >
                  <b-form-group
                    :label="$t('ประเภทการแทง')"
                    label-for="lotto-report-bet-type"
                  >
                    <b-form-select
                      id="lotto-report-bet-type"
                      v-model="filters.betType"
                      :options="betTypeOptions"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  v-if="activeView === 'items'"
                  cols="12"
                  sm="6"
                  md="3"
                  lg="2"
                >
                  <b-form-group
                    :label="$t('หมายเลข')"
                    label-for="lotto-report-number"
                  >
                    <b-form-input
                      id="lotto-report-number"
                      v-model.trim="filters.number"
                      inputmode="numeric"
                      maxlength="6"
                      :placeholder="$t('ค้นหาหมายเลข')"
                      @keydown.enter="applyFilters"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  v-if="activeView === 'items'"
                  cols="12"
                  sm="6"
                  md="3"
                  lg="2"
                >
                  <b-form-group
                    :label="$t('สมาชิก')"
                    label-for="lotto-report-member"
                  >
                    <b-form-input
                      id="lotto-report-member"
                      v-model.trim="filters.member"
                      maxlength="64"
                      :placeholder="$t('Username')"
                      @keydown.enter="applyFilters"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  v-if="activeView === 'items'"
                  cols="12"
                  sm="6"
                  md="3"
                  lg="2"
                >
                  <b-form-group
                    :label="$t('สถานะ')"
                    label-for="lotto-report-status"
                  >
                    <b-form-select
                      id="lotto-report-status"
                      v-model="filters.status"
                      :options="statusOptions"
                    />
                  </b-form-group>
                </b-col>

                <b-col
                  cols="12"
                  :lg="activeView === 'items' ? 2 : 'auto'"
                  class="filter-actions"
                >
                  <b-button
                    variant="primary"
                    class="filter-action-button"
                    :disabled="loading"
                    @click="applyFilters"
                  >
                    <feather-icon
                      icon="SearchIcon"
                      size="15"
                      class="mr-25"
                    />
                    {{ $t('ค้นหา') }}
                  </b-button>
                  <b-button
                    variant="outline-secondary"
                    class="filter-action-button"
                    :disabled="loading || !hasActiveFilters"
                    @click="resetFilters"
                  >
                    {{ $t('ล้าง') }}
                  </b-button>
                </b-col>
              </b-row>
            </div>
          </div>
        </div>

        <div
          class="report-table-shell"
          :aria-busy="loading ? 'true' : 'false'"
        >
          <div
            ref="tableViewport"
            class="report-height-viewport"
            :style="tableHeight === null ? null : { height: `${tableHeight}px` }"
          >
            <div
              ref="tableContent"
              class="report-height-content"
            >
              <b-table
                ref="reportTable"
                :items="rows"
                :fields="activeFields"
                :busy="loading"
                :data-report-view="activeView"
                :style="loading && loadingBodyHeight ? { '--report-loading-row-height': `${loadingBodyHeight}px` } : null"
                responsive
                stacked="sm"
                class="report-table report-table-colors report-table-header report-table-hover mb-0"
              >
                <template #table-colgroup="{ fields }">
                  <col
                    v-for="(field, index) in fields"
                    :key="field.key"
                    :style="loading && loadingColumnWidths[index] ? { width: `${loadingColumnWidths[index]}px` } : null"
                  >
                </template>
                <template #table-busy>
                  <div
                    class="report-loading-state"
                    role="status"
                    aria-live="polite"
                  >
                    <div class="report-loading-message">
                      <b-spinner
                        small
                        variant="primary"
                      />
                      <span>{{ $t('กำลังโหลดข้อมูล...') }}</span>
                    </div>
                  </div>
                </template>
                <template #cell(label)="data">
                  <div class="dimension-cell">
                    <strong>{{ data.item.label }}</strong>
                    <small
                      v-if="data.item.secondaryLabel"
                      class="text-muted d-block"
                    >
                      {{ data.item.secondaryLabel }}
                    </small>
                  </div>
                </template>

                <template #cell(betId)="data">
                  <div class="bill-id-cell">
                    <strong class="d-block text-body">{{ data.value || '-' }}</strong>
                    <small class="text-muted">{{ shortTxId(data.item.txId) }}</small>
                  </div>
                </template>

                <template #cell(username)="data">
                  <strong class="d-block text-body">{{ data.value || '-' }}</strong>
                  <small
                    v-if="data.item.prefix"
                    class="text-muted"
                  >
                    {{ data.item.prefix }}
                  </small>
                </template>

                <template #cell(betType)="data">
                  <span class="text-nowrap">{{ betTypeLabel(data.value) }}</span>
                </template>

                <template #cell(betNumber)="data">
                  <b-badge
                    variant="light-primary"
                    class="number-badge"
                  >
                    {{ data.value || '-' }}
                  </b-badge>
                </template>

                <template #cell(betTime)="data">
                  <span class="text-nowrap">{{ formatDate(data.value) }}</span>
                </template>

                <template #cell(status)="data">
                  <b-badge
                    :variant="statusVariant(data.value)"
                    :class="{ 'report-status-win': Number(data.value) === 1, 'report-status-loss': Number(data.value) === 2 }"
                    pill
                  >
                    {{ statusLabel(data.value) }}
                  </b-badge>
                </template>

                <template #cell(betAmount)="data">
                  <strong class="text-nowrap">{{ formatMoney(data.value) }}</strong>
                </template>

                <template #cell(commissionAmount)="data">
                  <span class="text-info text-nowrap">{{ formatMoney(data.value) }}</span>
                </template>

                <template #cell(payoutAmount)="data">
                  <span class="text-danger text-nowrap">{{ formatMoney(data.value) }}</span>
                </template>

                <template #cell(netAmount)="data">
                  <strong :class="[moneyColorClass(data.value), 'text-nowrap']">
                    {{ formatMoney(data.value) }}
                  </strong>
                </template>

                <template #cell(billCount)="data">
                  <span class="text-nowrap">{{ formatCount(data.value) }}</span>
                </template>

                <template #cell(itemCount)="data">
                  <span class="text-nowrap">{{ formatCount(data.value) }}</span>
                </template>

                <template #cell(hierarchy)="data">
                  <div class="hierarchy-breakdown">
                    <span class="share-pill">
                      <small>{{ $t('สมาชิก') }}</small>
                      <strong>{{ formatMoney(data.item.memberNetAmount) }}</strong>
                    </span>
                    <span class="share-pill">
                      <small>{{ $t('เอเย่น') }}</small>
                      <strong>{{ formatMoney(data.item.agentNetAmount) }}</strong>
                    </span>
                    <span class="share-pill">
                      <small>{{ $t('บริษัท') }}</small>
                      <strong>{{ formatMoney(data.item.companyNetAmount) }}</strong>
                    </span>
                  </div>
                </template>

                <template #cell(actions)="data">
                  <b-button
                    variant="outline-primary"
                    size="sm"
                    class="text-nowrap"
                    :disabled="!data.item.txId"
                    @click="openBill(data.item)"
                  >
                    <feather-icon
                      icon="EyeIcon"
                      size="14"
                      class="mr-25"
                    />
                    {{ $t('ดูบิล') }}
                  </b-button>
                </template>
              </b-table>

              <div
                v-if="!loading && rows.length === 0"
                class="table-state"
              >
                <template v-if="errorMessage || (!response.generatedAt && (showBuildProgress || buildStatusError || buildRequestError))">
                  <feather-icon
                    :icon="errorStateIcon"
                    size="38"
                    :class="[errorStateClass, 'mb-75']"
                  />
                  <strong class="d-block mb-25">{{ errorStateTitle }}</strong>
                  <span class="text-muted mb-1">{{ buildStatusError || buildRequestError || displayReportMessage }}</span>
                  <small
                    v-if="showBuildProgress && buildStatus && buildStatus.updatedAt"
                    class="text-muted mb-1"
                  >
                    {{ $t('อัปเดตสถานะล่าสุด') }} {{ formatDate(buildStatus.updatedAt) }}
                  </small>
                  <b-button
                    v-if="showBuildButton"
                    variant="outline-primary"
                    size="sm"
                    :disabled="!canRequestBuild || buildSubmitting"
                    @click="requestReportBuild"
                  >
                    <b-spinner
                      v-if="buildSubmitting || buildActive"
                      small
                      class="mr-25"
                    />
                    {{ buildSubmitting ? $t('กำลังส่งคำสั่ง') : buildActive ? buildTitle : buildRetryLabel }}
                  </b-button>
                  <b-button
                    v-else-if="canRetryError"
                    variant="outline-primary"
                    size="sm"
                    @click="fetchData"
                  >
                    <feather-icon
                      icon="RefreshCwIcon"
                      size="14"
                      class="mr-25"
                    />
                    {{ $t('ลองใหม่') }}
                  </b-button>
                  <b-button
                    v-else-if="showBackToResults"
                    variant="outline-secondary"
                    size="sm"
                    @click="goBackToResults"
                  >
                    <feather-icon
                      icon="ArrowLeftIcon"
                      size="14"
                      class="mr-25"
                    />
                    {{ $t('กลับไปเลือกรอบอื่น') }}
                  </b-button>
                </template>
                <template v-else>
                  <div class="empty-icon">
                    <feather-icon
                      icon="FileTextIcon"
                      size="30"
                    />
                  </div>
                  <strong class="d-block mb-25">{{ $t('ไม่พบข้อมูลในรอบนี้') }}</strong>
                  <span class="text-muted">{{ emptyDescription }}</span>
                </template>
              </div>
            </div>
          </div>
        </div>

        <div class="report-pagination">
          <div class="report-page-size">
            <label for="lotto-report-limit">{{ $t('reportPagination.perPage') }}</label>
            <b-form-select
              id="lotto-report-limit"
              v-model="limit"
              :options="limitOptions"
              size="sm"
              :disabled="loading"
              @change="onLimitChanged"
            />
          </div>
          <b-pagination
            :value="page"
            :total-rows="paginationTotalRows"
            :per-page="limit"
            :disabled="loading"
            first-number
            last-number
            class="mb-0"
            :aria-label="$t('reportPagination.navigation')"
            @change="onPageChanged"
          />
          <small
            class="report-pagination-summary"
            aria-live="polite"
            aria-atomic="true"
          >{{ paginationSummary }}</small>
        </div>
      </div>
    </b-card>

    <b-modal
      v-model="billModalVisible"
      size="xl"
      centered
      scrollable
      hide-footer
      body-class="bill-modal-body"
      modal-class="lotto-round-report-bill-modal hdg-report-theme"
      @hidden="clearBill"
    >
      <template #modal-title>
        <div>
          <strong>{{ $t('รายละเอียดบิล') }}</strong>
          <small
            v-if="bill.betId"
            class="text-muted ml-50"
          >
            #{{ bill.betId }}
          </small>
        </div>
      </template>

      <div
        v-if="billLoading"
        class="table-state"
      >
        <b-spinner
          variant="primary"
          class="mb-75"
        />
        <span>{{ $t('กำลังโหลดรายละเอียดบิล...') }}</span>
      </div>

      <div
        v-else-if="billError"
        class="table-state"
      >
        <feather-icon
          icon="AlertCircleIcon"
          size="38"
          class="text-danger mb-75"
        />
        <strong class="mb-25">{{ $t('ไม่สามารถโหลดรายละเอียดบิลได้') }}</strong>
        <span class="text-muted mb-1">{{ billError }}</span>
        <b-button
          v-if="billTargetRow"
          variant="outline-primary"
          size="sm"
          @click="openBill(billTargetRow)"
        >
          <feather-icon
            icon="RefreshCwIcon"
            size="14"
            class="mr-25"
          />
          {{ $t('ลองใหม่') }}
        </b-button>
      </div>

      <template v-else>
        <div class="bill-header">
          <div>
            <small class="text-muted d-block">{{ $t('สมาชิก') }}</small>
            <strong>{{ bill.username || '-' }}</strong>
            <span
              v-if="bill.prefix"
              class="text-muted ml-25"
            >({{ bill.prefix }})</span>
          </div>
          <div>
            <small class="text-muted d-block">{{ $t('เวลาแทง') }}</small>
            <strong>{{ formatDate(bill.betTime) }}</strong>
          </div>
          <div>
            <small class="text-muted d-block">{{ $t('สถานะ') }}</small>
            <b-badge
              :variant="statusVariant(bill.status)"
              :class="{ 'report-status-win': Number(bill.status) === 1, 'report-status-loss': Number(bill.status) === 2 }"
              pill
            >
              {{ statusLabel(bill.status) }}
            </b-badge>
          </div>
        </div>

        <div class="bill-metrics">
          <div
            v-for="metric in billMetricCards"
            :key="metric.key"
            class="bill-metric"
          >
            <small class="text-muted d-block">{{ metric.label }}</small>
            <strong :class="metric.valueClass">{{ metric.value }}</strong>
          </div>
        </div>

        <b-table
          :items="billItems"
          :fields="billFields"
          responsive
          show-empty
          stacked="sm"
          class="bill-items-table report-table-colors report-table-header report-table-hover mb-0"
        >
          <template #empty>
            <div class="text-center text-muted py-2">
              {{ $t('ไม่พบรายการในบิล') }}
            </div>
          </template>
          <template #cell(betType)="data">
            {{ betTypeLabel(data.value) }}
          </template>
          <template #cell(betNumber)="data">
            <b-badge variant="light-primary">
              {{ data.value || '-' }}
            </b-badge>
          </template>
          <template #cell(status)="data">
            <b-badge
              :variant="statusVariant(data.value)"
              :class="{ 'report-status-win': Number(data.value) === 1, 'report-status-loss': Number(data.value) === 2 }"
              pill
            >
              {{ statusLabel(data.value) }}
            </b-badge>
          </template>
          <template #cell(betAmount)="data">
            <strong>{{ formatMoney(data.value) }}</strong>
          </template>
          <template #cell(payRate)="data">
            {{ formatRate(data.value) }}
          </template>
          <template #cell(commissionAmount)="data">
            <span class="text-info">{{ formatMoney(data.value) }}</span>
          </template>
          <template #cell(payoutAmount)="data">
            <span class="text-danger">{{ formatMoney(data.value) }}</span>
          </template>
          <template #cell(netAmount)="data">
            <strong :class="moneyColorClass(data.value)">
              {{ formatMoney(data.value) }}
            </strong>
          </template>
        </b-table>
      </template>
    </b-modal>
  </section>
</template>

<script>
import {
  BAlert,
  BBadge,
  BButton,
  BCard,
  BCol,
  BFormCheckbox,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BInputGroup,
  BInputGroupPrepend,
  BModal,
  BPagination,
  BRow,
  BSpinner,
  BTable,
} from 'bootstrap-vue'
import { NumberFormatter } from '@core/utils/filter'
import { getCountryCode } from '@core/utils/countryFlag'
import AgentSelector from '@core/components/agent/AgentSelector.vue'
import lottoReportService from '@/service/lottoReportService'

const ALLOWED_VIEWS = ['type', 'number', 'member', 'items']
const MIN_BUILD_STAGE_MS = 250

// ป้ายประเภท 4/5 ตัว (ตรงกับ src/utils/betTypes.js) — ประกาศในไฟล์เพราะ test harness ของหน้านี้ตัด import ทิ้ง
const FOUR_FIVE_DIGIT_TYPE_LABELS = {
  41: 'สี่ตัวบน', 42: 'สี่ตัวโต๊ด', 51: 'ห้าตัวบน', 52: 'ห้าตัวโต๊ด',
}

const initialFilters = () => ({
  search: '',
  betType: '',
  number: '',
  member: '',
  status: '',
})

export default {
  name: 'LottoRoundReport',
  components: {
    AgentSelector,
    BAlert,
    BBadge,
    BButton,
    BCard,
    BCol,
    BFormCheckbox,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BInputGroup,
    BInputGroupPrepend,
    BModal,
    BPagination,
    BRow,
    BSpinner,
    BTable,
  },
  data() {
    const routeView = String(this.$route.query.view || 'type')
    const initialView = ALLOWED_VIEWS.includes(routeView) ? routeView : 'type'
    const isMaster = this.$store.state.app.isMaster(this.$store)
    const agent = isMaster && this.$route.query.agent
      ? String(this.$route.query.agent)
      : ''

    return {
      tabs: [
        {
          key: 'type',
          label: this.$t('ยอดตามประเภท'),
          description: this.$t('สรุปยอดแทงและผลลัพธ์แยกตามประเภทการแทง'),
          icon: 'BarChart2Icon',
          variant: 'primary',
        },
        {
          key: 'number',
          label: this.$t('ยอดตามหมายเลข'),
          description: this.$t('วิเคราะห์ยอดและความเสี่ยงของแต่ละหมายเลข'),
          icon: 'HashIcon',
          variant: 'success',
        },
        {
          key: 'member',
          label: this.$t('ยอดตามสมาชิก'),
          description: this.$t('สรุปผลประกอบการแยกตามสมาชิก'),
          icon: 'UsersIcon',
          variant: 'danger',
        },
        {
          key: 'items',
          label: this.$t('ยอดตามรายการ'),
          description: this.$t('ตรวจสอบรายการแทงและเปิดดูรายละเอียดแต่ละบิล'),
          icon: 'ListIcon',
          variant: 'warning',
        },
      ],
      activeView: initialView,
      selectedAgent: agent ? {
        code: agent,
        username: agent,
        label: agent,
      } : null,
      includeDownlines: this.$route.query.includeDownlines === 'true',
      filters: initialFilters(),
      response: {},
      rows: [],
      seenFourFiveDigitTypes: [],
      loading: true,
      controlsHeight: null,
      tableHeight: null,
      animateControlsHeight: false,
      layoutObserver: null,
      loadingBodyHeight: 0,
      loadingColumnWidths: [],
      reportDisposed: false,
      buildStatus: null,
      buildSubmitting: false,
      buildRequestError: '',
      buildStatusError: '',
      buildPollTimer: null,
      buildPollGeneration: 0,
      lastAutoLoadedGeneration: null,
      errorCode: '',
      errorMessage: '',
      limit: 50,
      limitOptions: [10, 25, 50, 100],
      currentCursor: null,
      nextCursor: null,
      cursorHistory: [],
      page: 1,
      requestId: 0,
      billModalVisible: false,
      billLoading: false,
      billError: '',
      bill: {},
      billTargetRow: null,
      billRequestId: 0,
      statusOptions: [
        { value: '', text: this.$t('ทุกสถานะ') },
        { value: 0, text: this.$t('รอผล') },
        { value: 1, text: this.$t('ถูกรางวัล') },
        { value: 2, text: this.$t('ไม่ถูกรางวัล') },
      ],
    }
  },
  computed: {
    huayId() {
      return String(this.$route.params.huayId || '')
    },
    roundId() {
      return String(this.$route.params.roundId || '')
    },
    huayName() {
      return String(this.$route.query.huayName || this.huayId)
    },
    huayType() {
      return Number(this.$route.query.huayType || 0)
    },
    roundDate() {
      return String(this.$route.query.roundDate || '')
    },
    countryCode() {
      return getCountryCode(this.huayName)
    },
    isMaster() {
      return this.$store.state.app.isMaster(this.$store)
    },
    reportRoundKey() {
      return `${this.huayId}:${this.roundId}`
    },
    buildActive() {
      return this.buildStatus && ['queued', 'running'].includes(this.buildStatus.status)
    },
    buildProblem() {
      return Boolean(this.buildStatusError || this.buildRequestError
        || (this.buildStatus && ['failed', 'stalled'].includes(this.buildStatus.status)))
    },
    showBuildProgress() {
      return this.buildStatus && ['queued', 'running', 'failed', 'stalled'].includes(this.buildStatus.status)
    },
    showReportBuildBanner() {
      return Boolean(this.response.generatedAt
        && (this.buildSubmitting || this.buildStatusError || this.buildRequestError || this.showBuildProgress))
    },
    canManageReportBuild() {
      return this.isMaster && this.$can('manage', 'all')
    },
    canRequestBuild() {
      return this.canManageReportBuild && this.buildStatus
        && this.buildStatus.canRequest && !this.buildStatusError
    },
    showBuildButton() {
      return this.canManageReportBuild
        && (this.canRequestBuild || this.buildActive || this.buildSubmitting)
    },
    canRebuildReport() {
      return Boolean(this.response.generatedAt && this.canRequestBuild && !this.buildSubmitting)
    },
    buildRetryLabel() {
      return this.$t(this.buildStatus && ['failed', 'stalled'].includes(this.buildStatus.status)
        ? 'สร้างรายงานอีกครั้ง' : 'สร้างรายงาน')
    },
    buildTitle() {
      const titles = {
        idle: 'ยังไม่ได้สร้างรายงาน',
        queued: 'รอเริ่มสร้างรายงาน',
        running: 'กำลังสร้างรายงาน',
        done: 'สร้างรายงานเสร็จแล้ว',
        failed: 'สร้างรายงานไม่สำเร็จ',
        stalled: 'งานสร้างรายงานไม่มีความคืบหน้า',
      }
      return this.$t(titles[this.buildStatus && this.buildStatus.status] || 'กำลังตรวจสอบสถานะรายงาน')
    },
    buildBannerTitle() {
      if (this.buildStatusError || this.buildRequestError) return this.buildStatusError || this.buildRequestError
      if (this.buildSubmitting) return this.$t('กำลังส่งคำสั่ง')
      return this.buildStatus && this.buildStatus.status === 'running'
        ? this.buildDescription : this.buildTitle
    },
    buildDescription() {
      const job = this.buildStatus || {}
      if (job.status === 'failed') return job.error || this.$t('กรุณาให้ Master สั่งสร้างรายงานอีกครั้ง')
      if (job.status === 'stalled') return this.$t('งานหยุดอัปเดตสถานะ กรุณาลองสั่งใหม่เมื่อปุ่มพร้อมใช้งาน หรือติดต่อผู้ดูแลระบบ')
      if (job.status === 'queued') return this.$t('ส่งคำสั่งแล้ว รอเริ่มประมวลผล หน้านี้จะอัปเดตอัตโนมัติ')
      if (job.status === 'running') {
        const phases = {
          checking: 'กำลังตรวจสอบข้อมูลต้นทาง',
          aggregating: 'กำลังคำนวณรายการ',
          'counting-bills': 'กำลังสรุปจำนวนบิล',
          validating: 'กำลังตรวจสอบความครบถ้วน',
          publishing: 'กำลังบันทึกรายงาน',
        }
        const phase = this.$t(phases[job.phase] || 'กำลังสร้างรายงาน')
        return job.total > 0 ? `${phase} ${this.formatCount(job.done)} / ${this.formatCount(job.total)}` : phase
      }
      return this.$t(this.canRequestBuild
        ? 'กดสร้างรายงานเพื่อเริ่มงานสำหรับทุกตัวแทนในรอบนี้'
        : 'รอระบบสร้างรายงาน หรือให้ Master กดสั่งสร้างรายงาน')
    },
    displayReportMessage() {
      return this.errorCode === 'REPORT_PROCESSING' || this.showBuildProgress
        ? this.buildDescription : this.errorMessage
    },
    activeTab() {
      return this.tabs.find(tab => tab.key === this.activeView) || this.tabs[0]
    },
    paginationTotalRows() {
      // Drives reachable page buttons only; cursor pagination has no result total.
      const reachablePages = this.page + (this.nextCursor ? 1 : 0)
      return reachablePages * Number(this.limit)
    },
    paginationSummary() {
      if (this.loading) return this.$t('กำลังโหลดข้อมูล...')
      if (this.errorMessage) return '—'
      if (!this.rows.length) return this.$t('reportPagination.empty')
      const start = (this.page - 1) * Number(this.limit) + 1
      return this.$t('reportPagination.showingItems', {
        start: this.formatCount(start),
        end: this.formatCount(start + this.rows.length - 1),
      })
    },
    activeFields() {
      if (this.activeView === 'items') return this.itemFields

      return [
        {
          key: 'label',
          label: this.dimensionLabel,
          class: 'dimension-column',
        },
        { key: 'betAmount', label: this.$t('ยอดแทง'), class: 'text-right' },
        { key: 'commissionAmount', label: this.$t('ค่าคอม'), class: 'text-right' },
        { key: 'payoutAmount', label: this.$t('ยอดจ่าย'), class: 'text-right' },
        { key: 'netAmount', label: this.$t('สุทธิ'), class: 'text-right' },
        { key: 'billCount', label: this.$t('บิล'), class: 'text-right' },
        { key: 'itemCount', label: this.$t('รายการ'), class: 'text-right' },
        {
          key: 'hierarchy', label: this.$t('ส่วนแบ่งสุทธิ'), class: 'hierarchy-column', thClass: 'text-center',
        },
      ]
    },
    itemFields() {
      return [
        { key: 'betId', label: this.$t('เลขที่บิล'), class: 'text-nowrap' },
        { key: 'username', label: this.$t('สมาชิก'), class: 'text-nowrap' },
        { key: 'betType', label: this.$t('ประเภท'), class: 'text-nowrap' },
        { key: 'betNumber', label: this.$t('หมายเลข'), class: 'text-center' },
        { key: 'betTime', label: this.$t('เวลาแทง'), class: 'text-nowrap' },
        { key: 'status', label: this.$t('สถานะ'), class: 'text-center' },
        { key: 'betAmount', label: this.$t('ยอดแทง'), class: 'text-right' },
        { key: 'commissionAmount', label: this.$t('ค่าคอม'), class: 'text-right' },
        { key: 'payoutAmount', label: this.$t('ยอดจ่าย'), class: 'text-right' },
        { key: 'netAmount', label: this.$t('สุทธิ'), class: 'text-right' },
        { key: 'actions', label: this.$t('จัดการ'), class: 'text-center' },
      ]
    },
    billFields() {
      return [
        { key: 'betType', label: this.$t('ประเภท'), class: 'text-nowrap' },
        { key: 'betNumber', label: this.$t('หมายเลข'), class: 'text-center' },
        { key: 'payRate', label: this.$t('อัตราจ่าย'), class: 'text-right' },
        { key: 'betAmount', label: this.$t('ยอดแทง'), class: 'text-right' },
        { key: 'commissionAmount', label: this.$t('ค่าคอม'), class: 'text-right' },
        { key: 'payoutAmount', label: this.$t('ยอดจ่าย'), class: 'text-right' },
        { key: 'netAmount', label: this.$t('สุทธิ'), class: 'text-right' },
        { key: 'status', label: this.$t('สถานะ'), class: 'text-center' },
      ]
    },
    billItems() {
      return Array.isArray(this.bill.items) ? this.bill.items : []
    },
    dimensionLabel() {
      if (this.activeView === 'number') return this.$t('หมายเลข')
      if (this.activeView === 'member') return this.$t('สมาชิก')
      return this.$t('ประเภทการแทง')
    },
    searchLabel() {
      return this.activeView === 'member' ? this.$t('ค้นหาสมาชิก') : this.$t('ค้นหาหมายเลข')
    },
    searchPlaceholder() {
      return this.activeView === 'member'
        ? this.$t('ค้นหา username หรือ prefix')
        : this.$t('ค้นหาหมายเลข')
    },
    betTypeOptions() {
      const options = [{ value: '', text: this.$t('ทุกประเภท') }]
      // ไม่รู้ประเภทหวย = ยังมีเลขชุด (9) เหมือนเดิม · 10/11 และ 9 ของรัฐบาลเป็นของเก่าที่ตัดทิ้ง (FOUR_FIVE_DIGIT_BETS ข้อ 11)
      const maxType = this.huayType ? 8 : 9
      for (let value = 1; value <= maxType; value += 1) {
        options.push({ value, text: this.betTypeLabel(value) })
      }
      // 4/5 ตัว: แสดงเฉพาะประเภทที่รอบนี้มีโพย (หรือเลือกค้างไว้) — หวยที่ไม่ได้เปิดใช้จึงเหมือนเดิมทุกประการ
      const fourFiveDigitTypes = new Set(this.seenFourFiveDigitTypes)
      if (FOUR_FIVE_DIGIT_TYPE_LABELS[this.filters.betType]) fourFiveDigitTypes.add(Number(this.filters.betType))
      Object.keys(FOUR_FIVE_DIGIT_TYPE_LABELS).map(Number)
        .filter(value => fourFiveDigitTypes.has(value))
        .forEach(value => options.push({ value, text: this.betTypeLabel(value) }))

      if (this.huayType === 5) {
        return [
          options[0],
          { value: 9, text: this.$t('เลขชุด') },
        ]
      }

      return options
    },
    hasActiveFilters() {
      return Object.values(this.filters).some(value => value !== '' && value !== null)
    },
    displayTotals() {
      if (this.response.totals) return this.normalizeMetrics(this.response.totals)

      return this.rows.reduce((totals, row) => ({
        betAmount: totals.betAmount + this.toNumber(row.betAmount),
        commissionAmount: totals.commissionAmount + this.toNumber(row.commissionAmount),
        payoutAmount: totals.payoutAmount + this.toNumber(row.payoutAmount),
        netAmount: totals.netAmount + this.toNumber(row.netAmount),
      }), this.normalizeMetrics({}))
    },
    showSummary() {
      return !this.loading && (!!this.response.totals || Array.isArray(this.response.items))
    },
    summaryScopeLabel() {
      if (!this.showSummary) return '—'
      if (!this.response.totals) return this.$t('ยอดรวมในหน้านี้')
      return this.$t('ยอดรวมทั้งรอบ')
    },
    summaryCards() {
      const totals = this.showSummary ? this.displayTotals : this.normalizeMetrics({})
      return [
        {
          key: 'betAmount',
          label: this.$t('ยอดแทง'),
          value: this.formatMoney(totals.betAmount),
          icon: 'TrendingUpIcon',
          variant: 'primary',
          valueClass: '',
        },
        {
          key: 'commissionAmount',
          label: this.$t('ค่าคอม'),
          value: this.formatMoney(totals.commissionAmount),
          icon: 'PercentIcon',
          variant: 'info',
          valueClass: 'text-info',
        },
        {
          key: 'payoutAmount',
          label: this.$t('ยอดจ่าย'),
          value: this.formatMoney(totals.payoutAmount),
          icon: 'AwardIcon',
          variant: 'danger',
          valueClass: 'text-danger',
        },
        {
          key: 'netAmount',
          label: this.$t('สุทธิ'),
          value: this.formatMoney(totals.netAmount),
          icon: 'DollarSignIcon',
          variant: this.toNumber(totals.netAmount) < 0 ? 'danger' : 'success',
          valueClass: this.moneyColorClass(totals.netAmount),
        },
      ]
    },
    billMetricCards() {
      const metrics = this.normalizeMetrics(this.bill.metrics || {})
      return [
        {
          key: 'betAmount',
          label: this.$t('ยอดแทง'),
          value: this.formatMoney(metrics.betAmount),
          valueClass: '',
        },
        {
          key: 'commissionAmount',
          label: this.$t('ค่าคอม'),
          value: this.formatMoney(metrics.commissionAmount),
          valueClass: 'text-info',
        },
        {
          key: 'payoutAmount',
          label: this.$t('ยอดจ่าย'),
          value: this.formatMoney(metrics.payoutAmount),
          valueClass: 'text-danger',
        },
        {
          key: 'netAmount',
          label: this.$t('สุทธิ'),
          value: this.formatMoney(metrics.netAmount),
          valueClass: this.moneyColorClass(metrics.netAmount),
        },
      ]
    },
    emptyDescription() {
      if (this.hasActiveFilters) return this.$t('ลองเปลี่ยนตัวกรองแล้วค้นหาอีกครั้ง')
      return this.$t('รอบนี้ยังไม่มีรายการที่ตรงกับขอบเขตที่เลือก')
    },
    errorStateTitle() {
      if (this.errorCode === 'REPORT_PROCESSING' || this.showBuildProgress) return this.buildTitle
      if (this.errorCode === 'REPORT_NOT_AVAILABLE') return this.$t('ไม่มีรายงานย้อนหลังสำหรับรอบนี้')
      if (this.errorCode === 'ROUND_NOT_FINALIZED') return this.$t('รอบหวยยังไม่สิ้นสุด')
      if (this.errorCode === 'ROUND_NOT_FOUND') return this.$t('ไม่พบรอบหวยนี้')
      if (this.errorCode === 'REPORT_PROCESSING') return this.$t('กำลังจัดเตรียมรายงาน')
      return this.$t('ไม่สามารถโหลดรายงานได้')
    },
    errorStateIcon() {
      if (this.buildProblem) return 'AlertCircleIcon'
      if (this.errorCode === 'REPORT_NOT_AVAILABLE') return 'ArchiveIcon'
      if (this.errorCode === 'ROUND_NOT_FINALIZED' || this.errorCode === 'REPORT_PROCESSING') return 'ClockIcon'
      return 'AlertCircleIcon'
    },
    errorStateClass() {
      if (this.buildProblem) return 'text-warning'
      if (this.errorCode === 'REPORT_NOT_AVAILABLE') return 'text-secondary'
      if (this.errorCode === 'ROUND_NOT_FINALIZED' || this.errorCode === 'REPORT_PROCESSING') return 'text-warning'
      return 'text-danger'
    },
    canRetryError() {
      return !this.showBuildProgress && !['REPORT_PROCESSING', 'REPORT_NOT_AVAILABLE', 'ROUND_NOT_FINALIZED', 'ROUND_NOT_FOUND'].includes(this.errorCode)
    },
    showBackToResults() {
      return ['REPORT_NOT_AVAILABLE', 'ROUND_NOT_FINALIZED', 'ROUND_NOT_FOUND'].includes(this.errorCode)
    },
  },
  watch: {
    reportRoundKey() {
      this.buildStatus = null
      this.buildRequestError = ''
      this.buildStatusError = ''
      this.buildSubmitting = false
      this.lastAutoLoadedGeneration = null
      this.seenFourFiveDigitTypes = []
      this.resetPagination()
      this.fetchData()
      this.startBuildPolling()
    },
  },
  mounted() {
    if (!this.huayId || !this.roundId) {
      this.$router.replace({ name: 'dashboard-result' })
      return
    }

    if (!this.isMaster) this.selectedAgent = null
    this.initializeReportLayout()
    this.syncRouteQuery()
    this.fetchData()
    this.startBuildPolling()
  },
  beforeDestroy() {
    this.reportDisposed = true
    if (this.layoutObserver) this.layoutObserver.disconnect()
    this.buildPollGeneration += 1
    clearTimeout(this.buildPollTimer)
    this.requestId += 1
    this.billRequestId += 1
  },
  methods: {
    initializeReportLayout() {
      if (typeof ResizeObserver === 'undefined') return
      this.layoutObserver = new ResizeObserver(() => this.measureReportLayout())
      this.measureReportLayout()
      const elements = [this.$refs.reportControls, this.$refs.tableContent]
      elements.filter(Boolean).forEach(element => {
        this.layoutObserver.observe(element)
      })
    },
    measureReportLayout() {
      if (this.reportDisposed || !this.layoutObserver) return
      // The new tab's controls use natural height immediately. Keep measuring
      // both regions so data can resize the table smoothly from its loading box.
      const elements = [
        ['controlsHeight', this.$refs.reportControls],
        ['tableHeight', this.$refs.tableContent],
      ]
      elements.forEach(([heightProperty, element]) => {
        const height = element?.getBoundingClientRect().height
        if (!Number.isFinite(height) || height < 0 || height === this[heightProperty]) return
        this[heightProperty] = height
      })
    },
    captureReportTableLayout() {
      const table = this.$refs.reportTable?.$el
      const header = table?.querySelector('thead')
      const body = table?.querySelector('tbody')
      const headerHeight = header?.getBoundingClientRect().height || 0
      const bodyHeight = body?.getBoundingClientRect().height || 0
      const contentHeight = this.$refs.tableContent?.getBoundingClientRect().height || 0
      const height = this.$refs.tableViewport?.getBoundingClientRect().height || 0
      // Preserve the actual row box, including fractional pixels. If a resize is
      // in progress, hold the visible height; empty/error states live below tbody.
      this.loadingBodyHeight = Math.max(0, bodyHeight > 0 && (this.loading || this.rows.length)
        ? bodyHeight + height - contentHeight
        : height - headerHeight)
      // Only preserve column widths when the fields belong to the same tab.
      this.loadingColumnWidths = headerHeight > 0 && table?.getAttribute('data-report-view') === this.activeView
        ? Array.from(header.querySelectorAll('tr > th')).map(cell => cell.getBoundingClientRect().width)
        : []
    },
    async waitForBuildStage() {
      await this.$nextTick()
      // Give the current state a painted frame before starting its minimum display time.
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)))
      await new Promise(resolve => setTimeout(resolve, MIN_BUILD_STAGE_MS))
    },
    startBuildPolling() {
      if (this.reportDisposed) return
      clearTimeout(this.buildPollTimer)
      this.buildPollGeneration += 1
      this.pollBuildStatus(this.buildPollGeneration)
    },
    async pollBuildStatus(generation) {
      try {
        const res = await lottoReportService.getBuildStatus(this.huayId, this.roundId, this.commonParams())
        if (generation !== this.buildPollGeneration) return
        await this.waitForBuildStage()
        if (generation !== this.buildPollGeneration) return
        if ([400, 403, 404].includes(res.status)) {
          this.buildStatus = null
          this.buildStatusError = ''
          this.buildPollGeneration += 1
          return
        }
        if (res.status !== 200) {
          this.buildStatusError = this.$t('ไม่สามารถติดตามสถานะรายงานได้ กำลังลองเชื่อมต่ออีกครั้ง')
          return
        }
        this.buildStatusError = ''
        this.buildStatus = res.data
        if (['queued', 'running', 'done'].includes(res.data.status)) this.buildRequestError = ''
        if (!this.loading && res.data.status === 'done' && res.data.generatedAt
          && res.data.generatedAt !== this.response.generatedAt
          && res.data.generatedAt !== this.lastAutoLoadedGeneration) {
          this.lastAutoLoadedGeneration = res.data.generatedAt
          this.resetPagination()
          await this.fetchData()
        }
      } catch (error) {
        if (generation !== this.buildPollGeneration) return
        await this.waitForBuildStage()
        if (generation === this.buildPollGeneration) {
          this.buildStatusError = this.$t('ไม่สามารถติดตามสถานะรายงานได้ กำลังลองเชื่อมต่ออีกครั้ง')
        }
      } finally {
        if (generation === this.buildPollGeneration) {
          this.buildPollTimer = setTimeout(() => this.pollBuildStatus(generation), this.buildActive ? 3000 : 10000)
        }
      }
    },
    async requestReportBuild() {
      if (!this.canRequestBuild || this.buildSubmitting) return
      const roundKey = this.reportRoundKey
      clearTimeout(this.buildPollTimer)
      this.buildPollGeneration += 1
      this.buildSubmitting = true
      this.buildRequestError = ''
      try {
        const res = await lottoReportService.requestBuild(this.huayId, this.roundId)
        if (this.reportDisposed || roundKey !== this.reportRoundKey) return
        await this.waitForBuildStage()
        if (this.reportDisposed || roundKey !== this.reportRoundKey) return
        if (res.status === 202 || res.status === 200) {
          this.buildStatus = {
            ...this.buildStatus, status: 'queued', canRequest: false, requestId: res.data.requestId,
          }
        } else {
          this.buildRequestError = (res.data && res.data.message) || this.$t('ส่งคำสั่งสร้างรายงานไม่สำเร็จ กรุณาลองใหม่')
        }
      } catch (error) {
        if (this.reportDisposed || roundKey !== this.reportRoundKey) return
        await this.waitForBuildStage()
        if (!this.reportDisposed && roundKey === this.reportRoundKey) {
          this.buildRequestError = this.$t('ยังยืนยันการส่งคำสั่งไม่ได้ กำลังตรวจสอบสถานะงาน')
        }
      } finally {
        if (!this.reportDisposed && roundKey === this.reportRoundKey) {
          this.buildSubmitting = false
          this.startBuildPolling()
        }
      }
    },

    goBackToResults() {
      this.$router.push({ name: 'dashboard-result' })
    },
    selectView(view) {
      if (!ALLOWED_VIEWS.includes(view) || view === this.activeView) return
      this.activeView = view
      this.filters = initialFilters()
      this.resetPagination()
      this.syncRouteQuery()
      this.fetchData()
    },
    onAgentChanged() {
      this.buildStatus = null
      this.startBuildPolling()
      this.resetPagination()
      this.syncRouteQuery()
      this.fetchData()
    },
    onScopeChanged() {
      this.buildStatus = null
      this.startBuildPolling()
      this.resetPagination()
      this.syncRouteQuery()
      this.fetchData()
    },
    syncRouteQuery() {
      const query = {
        ...this.$route.query,
        view: this.activeView,
      }
      const agent = this.selectedAgent && this.selectedAgent.code

      if (this.isMaster && agent) query.agent = agent
      else delete query.agent

      if (this.includeDownlines) query.includeDownlines = 'true'
      else delete query.includeDownlines

      this.$router.replace({
        name: 'dashboard-result-report',
        params: {
          huayId: this.huayId,
          roundId: this.roundId,
        },
        query,
      }).catch(() => {})
    },
    applyFilters() {
      this.resetPagination()
      this.fetchData()
    },
    resetFilters() {
      this.filters = initialFilters()
      this.resetPagination()
      this.fetchData()
    },
    onLimitChanged() {
      this.resetPagination()
      this.fetchData()
    },
    resetPagination() {
      this.currentCursor = null
      this.nextCursor = null
      this.cursorHistory = []
      this.page = 1
    },
    goNext() {
      if (!this.nextCursor || this.loading) return
      this.cursorHistory.push(this.currentCursor)
      this.currentCursor = this.nextCursor
      this.page += 1
      this.fetchData()
    },
    goPrevious() {
      if (this.cursorHistory.length === 0 || this.loading) return
      this.currentCursor = this.cursorHistory.pop()
      this.page = Math.max(1, this.page - 1)
      this.fetchData()
    },
    onPageChanged(targetPage) {
      const requestedPage = Number(targetPage)
      if (!Number.isInteger(requestedPage) || requestedPage < 1 || requestedPage === this.page || this.loading) return

      if (requestedPage === this.page + 1) {
        this.goNext()
        return
      }

      if (requestedPage < this.page) {
        this.currentCursor = this.cursorHistory[requestedPage - 1]
        this.cursorHistory = this.cursorHistory.slice(0, requestedPage - 1)
        this.page = requestedPage
        this.fetchData()
      }
    },
    commonParams() {
      const params = {
        includeDownlines: this.includeDownlines,
        limit: this.limit,
        cursor: this.currentCursor,
      }
      if (this.isMaster && this.selectedAgent && this.selectedAgent.code) {
        params.agent = this.selectedAgent.code
      }
      return params
    },
    requestForActiveView() {
      const params = this.commonParams()

      if (this.activeView === 'number') {
        return lottoReportService.getByNumber(this.huayId, this.roundId, {
          ...params,
          betType: this.filters.betType,
          search: this.filters.search,
        })
      }
      if (this.activeView === 'member') {
        return lottoReportService.getByMember(this.huayId, this.roundId, {
          ...params,
          search: this.filters.search,
        })
      }
      if (this.activeView === 'items') {
        return lottoReportService.getItems(this.huayId, this.roundId, {
          ...params,
          betType: this.filters.betType,
          number: this.filters.number,
          member: this.filters.member,
          status: this.filters.status,
        })
      }
      return lottoReportService.getByType(this.huayId, this.roundId, params)
    },
    async fetchData() {
      // Switch the tab's controls immediately rather than animating them first
      // and the table later. The table always interpolates its measured height
      // when data arrives; neither rendering nor the request waits on a timer.
      this.animateControlsHeight = !this.loading
        && this.$refs.reportTable?.$el?.getAttribute('data-report-view') === this.activeView
      this.captureReportTableLayout()
      const requestId = this.requestId + 1
      this.requestId = requestId
      this.loading = true
      this.errorCode = ''
      this.errorMessage = ''
      this.rows = []

      try {
        const res = await this.requestForActiveView()
        if (requestId !== this.requestId) return

        if (![200, 202].includes(res.status)) {
          this.response = {}
          this.nextCursor = null
          const errorData = res.data && typeof res.data === 'object' ? res.data : {}
          this.errorCode = errorData.code || ''
          this.errorMessage = this.resolveErrorMessage(res.status, this.errorCode)
          return
        }

        const responseData = res.data && typeof res.data === 'object' ? res.data : {}
        this.response = {
          ...responseData,
          isComplete: res.status === 202 ? false : responseData.isComplete,
        }
        const items = Array.isArray(this.response.items) ? this.response.items : []
        this.rows = this.activeView === 'items'
          ? items.map(this.normalizeItemRow)
          : items.map(this.normalizeAggregateRow)
        this.rememberFourFiveDigitTypes(this.rows)
        this.nextCursor = this.response.nextCursor || null
      } catch (error) {
        if (requestId !== this.requestId) return
        this.response = {}
        this.nextCursor = null
        this.errorCode = 'NETWORK_ERROR'
        this.errorMessage = this.$t('ตรวจสอบการเชื่อมต่อแล้วลองใหม่อีกครั้ง')
      } finally {
        if (requestId === this.requestId) {
          this.loading = false
          this.$nextTick(() => {
            if (requestId !== this.requestId || this.reportDisposed) return
            this.measureReportLayout()
          })
        }
      }
    },
    normalizeAggregateRow(item) {
      const metrics = this.normalizeMetrics(item.metrics || {})
      return {
        ...item,
        label: item.label || item.key || '-',
        secondaryLabel: this.activeView !== 'type' && item.betType ? this.betTypeLabel(item.betType) : '',
        ...metrics,
        memberNetAmount: this.toNumber(metrics.member.netAmount),
        agentNetAmount: this.toNumber(metrics.agentShare.netAmount),
        companyNetAmount: this.toNumber(metrics.company.netAmount),
      }
    },
    normalizeItemRow(item) {
      return {
        ...item,
        betAmount: this.toNumber(item.betAmount),
        commissionAmount: this.toNumber(item.commissionAmount),
        payoutAmount: this.toNumber(item.payoutAmount),
        netAmount: this.toNumber(item.netAmount),
      }
    },
    normalizeMetrics(metrics) {
      return {
        betAmount: this.toNumber(metrics.betAmount),
        commissionAmount: this.toNumber(metrics.commissionAmount),
        payoutAmount: this.toNumber(metrics.payoutAmount),
        netAmount: this.toNumber(metrics.netAmount),
        billCount: this.toNumber(metrics.billCount),
        itemCount: this.toNumber(metrics.itemCount),
        member: metrics.member || {},
        agentShare: metrics.agentShare || {},
        company: metrics.company || {},
      }
    },
    async openBill(row) {
      if (!row.txId) return

      this.billTargetRow = row
      this.billModalVisible = true
      this.billLoading = true
      this.billError = ''
      this.bill = { betId: row.betId }
      const requestId = this.billRequestId + 1
      this.billRequestId = requestId

      const params = {}
      if (this.isMaster && this.selectedAgent && this.selectedAgent.code) {
        params.agent = this.selectedAgent.code
      }
      params.includeDownlines = this.includeDownlines

      try {
        const res = await lottoReportService.getBillByRow(row.txId, params)
        if (requestId !== this.billRequestId) return

        if (res.status !== 200) {
          this.billError = this.resolveErrorMessage(res.status)
          return
        }
        this.bill = res.data || {}
      } catch (error) {
        if (requestId !== this.billRequestId) return
        this.billError = this.$t('ตรวจสอบการเชื่อมต่อแล้วลองใหม่อีกครั้ง')
      } finally {
        if (requestId === this.billRequestId) this.billLoading = false
      }
    },
    clearBill() {
      this.billRequestId += 1
      this.billLoading = false
      this.billError = ''
      this.bill = {}
      this.billTargetRow = null
    },
    resolveErrorMessage(status, code) {
      if (code === 'REPORT_NOT_AVAILABLE' || status === 410) {
        return this.$t('รอบนี้อยู่นอกช่วงรายงานย้อนหลังที่ระบบจัดเตรียมไว้ กรุณาเลือกรอบอื่น')
      }
      if (code === 'ROUND_NOT_FINALIZED') return this.$t('รายงานจะพร้อมหลังจากรอบหวยสิ้นสุดแล้ว')
      if (code === 'ROUND_NOT_FOUND') return this.$t('รอบหวยนี้อาจถูกลบหรือข้อมูลรอบไม่ถูกต้อง')
      if (code === 'REPORT_PROCESSING') return this.$t('กำลังตรวจสอบสถานะรายงาน')
      if (status === 400) return this.$t('ตัวกรองหรือข้อมูลรอบไม่ถูกต้อง')
      if (status === 403) return this.$t('คุณไม่มีสิทธิ์ดูข้อมูลในขอบเขตนี้')
      if (status === 404) return this.$t('ไม่พบข้อมูลรอบหรือรายการที่ต้องการ')
      if (status === 409 || status === 425) return this.$t('รายงานกำลังประมวลผล กรุณาลองใหม่อีกครั้ง')
      return this.$t('ระบบไม่สามารถโหลดข้อมูลได้ กรุณาลองใหม่อีกครั้ง')
    },
    toNumber(value) {
      const number = Number(value)
      return Number.isFinite(number) ? number : 0
    },
    formatMoney(value) {
      return `฿${NumberFormatter(this.toNumber(value).toFixed(2))}`
    },
    formatCount(value) {
      return NumberFormatter(String(Math.trunc(this.toNumber(value))))
    },
    formatRate(value) {
      return NumberFormatter(this.toNumber(value).toFixed(2))
    },
    formatDate(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return String(value)
      return new Intl.DateTimeFormat('th-TH', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(date)
    },
    moneyColorClass(value) {
      const number = this.toNumber(value)
      if (number > 0) return 'text-success'
      if (number < 0) return 'text-danger'
      return ''
    },
    rememberFourFiveDigitTypes(rows) {
      const found = rows.map(row => Number(row.betType))
        .filter(value => FOUR_FIVE_DIGIT_TYPE_LABELS[value] && !this.seenFourFiveDigitTypes.includes(value))
      if (found.length) this.seenFourFiveDigitTypes = [...new Set([...this.seenFourFiveDigitTypes, ...found])]
    },
    betTypeLabel(value) {
      const labels = [
        'สามตัวบน',
        'สามตัวโต๊ด',
        'สามตัวหน้า',
        'สามตัวท้าย',
        'สองตัวบน',
        'สองตัวล่าง',
        'วิ่งบน',
        'วิ่งล่าง',
        'เลขชุด',
      ]
      return this.$t(labels[Number(value) - 1] || FOUR_FIVE_DIGIT_TYPE_LABELS[value] || String(value || '-'))
    },
    statusLabel(value) {
      const labels = {
        '-1': 'คืนโพย',
        0: 'รอผล',
        1: 'ถูกรางวัล',
        2: 'ไม่ถูกรางวัล',
      }
      return this.$t(labels[String(value)] || 'ไม่ทราบสถานะ')
    },
    statusVariant(value) {
      const variants = {
        '-1': 'light-secondary',
        0: 'light-warning',
        1: 'light-success',
        2: 'light-danger',
      }
      return variants[String(value)] || 'light-secondary'
    },
    shortTxId(value) {
      if (!value) return '-'
      const text = String(value)
      return text.length > 14 ? `${text.slice(0, 7)}…${text.slice(-5)}` : text
    },
  },
}
</script>

<style lang="scss" scoped>
.report-context-card,
.report-content-card {
  border: 1px solid var(--report-border);
  box-shadow: 0 8px 28px rgba(34, 41, 47, 0.06);
}
.report-context-header {
  align-items: center;
  display: flex;
  gap: 1rem;
  min-height: 94px;
  padding: 1.25rem 1.5rem;
}
.back-button {
  align-self: flex-start;
  border-radius: 50%;
  flex: 0 0 auto;
  height: 2rem;
  padding: 0;
  width: 2rem;
}
.report-title-wrap {
  display: grid;
  flex: 1 1 auto;
  gap: 0.35rem 0.75rem;
  grid-template-columns: 1.25rem minmax(0, 1fr);
  min-width: 0;
}
.report-country-flag {
  box-shadow: var(--report-flag-shadow);
  align-self: center;
  grid-column: 1;
  grid-row: 1;
  height: 1.25rem;
  width: 1.25rem;
}
.report-title {
  font-size: 1.35rem;
  grid-column: 2;
  grid-row: 1;
  line-height: 2rem;
}
.report-calendar-icon {
  align-self: center;
  color: var(--report-secondary-text);
  grid-column: 1;
  grid-row: 2;
  justify-self: center;
}
.report-round-meta {
  align-items: center;
  color: var(--report-secondary-text);
  display: flex;
  flex-wrap: wrap;
  font-size: 0.82rem;
  gap: 0.4rem 1rem;
  grid-column: 2;
  grid-row: 2;
}
.round-id {
  font-family: monospace;
  overflow-wrap: anywhere;
}
.report-round-action {
  display: flex;
  grid-column: 1 / -1;
  grid-row: 3;
}
.report-rebuild-button {
  align-items: center;
  border: 0;
  display: inline-grid;
  font-size: 0.82rem;
  font-weight: 400;
  gap: 0.75rem;
  grid-template-columns: 1.25rem minmax(0, 1fr);
  margin-left: -0.5rem;
  padding: 0.35rem 0.5rem;
  text-align: left;
}
.report-rebuild-button > svg {
  justify-self: center;
}
.report-scope-controls {
  align-items: stretch;
  align-self: center;
  display: flex;
  flex: 0 1 auto;
  gap: 1rem;
  margin-left: auto;
}
.agent-selector-wrap {
  display: flex;
  min-width: 250px;
  width: min(320px, 30vw);
}
.agent-selector-wrap ::v-deep .v-select {
  display: flex;
  width: 100%;
}
.agent-selector-wrap ::v-deep .vs-sm .vs__dropdown-toggle {
  align-items: center;
  flex: 1;
  height: auto;
  min-height: 38px;
}
.include-downlines-switch {
  margin-bottom: 0;
  min-height: 1.5rem;
  padding-left: 2.5rem;
}
.include-downlines-switch >>> .custom-control-label {
  margin-bottom: 0;
}
.include-downlines-control {
  align-items: center;
  background: var(--report-muted-bg);
  border: 1px solid var(--report-border);
  border-radius: 0.55rem;
  color: var(--report-control-text);
  display: inline-flex;
  font-weight: 600;
  gap: 0.65rem;
  justify-content: space-between;
  padding: 0.55rem 0.7rem;
  white-space: nowrap;
}
.report-content-card {
  overflow: hidden;
}
.report-tabs {
  background: var(--report-muted-bg);
  border-bottom: 1px solid var(--report-border);
  display: grid;
  grid-template-columns: repeat(4, minmax(150px, 1fr));
  overflow-x: auto;
  padding: 0;
}
.report-tab {
  align-items: center;
  background: transparent;
  border: 0;
  border-bottom: 3px solid transparent;
  color: var(--report-label-text);
  cursor: pointer;
  display: inline-flex;
  font: inherit;
  font-weight: 600;
  gap: 0.65rem;
  justify-content: center;
  min-height: 68px;
  outline: none;
  padding: 0.75rem 1rem;
  transition: background 0.2s ease, color 0.2s ease;
  white-space: nowrap;
}
.report-tab:hover,
.report-tab:focus-visible {
  background: var(--report-hover-bg);
  color: var(--report-action-text);
}
.report-tab.active {
  background: var(--report-panel-bg);
  border-bottom-color: var(--report-action-text);
  color: var(--report-action-text);
}
.report-tab:focus-visible {
  outline: 2px solid var(--report-action-text);
  outline-offset: -4px;
}
.tab-icon {
  align-items: center;
  display: inline-flex;
  justify-content: center;
  opacity: 0.8;
  transition: color 0.2s ease, opacity 0.2s ease;
}
.tab-icon-primary {
  color: var(--report-action-text);
}
.tab-icon-success {
  color: var(--report-success-text);
}
.tab-icon-danger {
  color: var(--report-danger-text);
}
.tab-icon-warning {
  color: var(--report-warning-text);
}
.report-tab:hover .tab-icon,
.report-tab:focus-visible .tab-icon {
  opacity: 1;
}
.report-tab.active .tab-icon {
  opacity: 1;
}
.report-body {
  padding: 1.5rem;
}
.report-height-viewport {
  overflow: hidden;
  transition: height 220ms cubic-bezier(0.2, 0.8, 0.2, 1);
}
.report-height-viewport--static {
  transition: none;
}
.report-height-content {
  display: flow-root;
}
.report-status-row {
  align-items: flex-start;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  margin-bottom: 1.25rem;
}
.report-metadata {
  align-items: flex-end;
  color: var(--report-secondary-text);
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  font-size: 0.82rem;
  gap: 0.25rem;
  min-width: 14rem;
  font-variant-numeric: tabular-nums;
}
.summary-grid {
  margin-left: -0.5rem;
  margin-right: -0.5rem;
}
.summary-grid > [class*='col-'] {
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}
.summary-card {
  align-items: center;
  background: var(--report-muted-bg);
  border: 1px solid var(--report-border);
  border-radius: 0.5rem;
  display: flex;
  gap: 0.85rem;
  min-height: 82px;
  padding: 1rem;
}
.summary-icon {
  align-items: center;
  display: inline-flex;
  flex: 0 0 auto;
  height: 42px;
  justify-content: center;
  width: 42px;
}
.summary-value {
  display: block;
  font-size: 1.08rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.report-filter-panel {
  background: var(--report-muted-bg);
  border: 1px solid var(--report-border);
  border-radius: 0.5rem;
  margin-bottom: 1.25rem;
  padding: 1rem 1rem 0;
}
.filter-actions {
  display: flex;
  gap: 0.5rem;
  padding-bottom: 1rem;
}
.filter-action-button {
  align-items: center;
  display: inline-flex;
  flex: 1 1 0;
  justify-content: center;
  min-height: 38px;
  min-width: 0;
  white-space: nowrap;
}
.report-table-shell {
  border: 1px solid var(--report-border);
  border-radius: 0.5rem;
  overflow: hidden;
}
.report-table[aria-busy='true'] {
  opacity: 1;
}
.report-table ::v-deep .b-table-busy-slot > td {
  position: relative;
  height: inherit;
  padding: 0;
  border-top: 0;
}
.report-table ::v-deep .b-table-busy-slot {
  height: var(--report-loading-row-height, var(--report-row-min-height));
}
.report-loading-state {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--report-body-text);
}
.report-loading-message {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
}
::v-deep .report-table thead th,
::v-deep .bill-items-table thead th {
  white-space: nowrap !important;
}
.report-table >>> tbody td,
.bill-items-table >>> tbody td {
  padding: 0.85rem 1rem;
  vertical-align: middle;
}
.report-table >>> .dimension-column {
  min-width: 190px;
}
.report-table >>> .hierarchy-column {
  min-width: 250px;
}
.table-state {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 230px;
  padding: 2rem 1rem;
  text-align: center;
}
.empty-icon {
  align-items: center;
  background: var(--report-empty-bg);
  border-radius: 50%;
  color: var(--report-action-text);
  display: inline-flex;
  height: 58px;
  justify-content: center;
  margin-bottom: 0.85rem;
  width: 58px;
}
.number-badge {
  font-family: monospace;
  font-size: 0.9rem;
  letter-spacing: 0.08em;
}
.hierarchy-breakdown {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  justify-content: center;
}
.share-pill {
  background: var(--report-share-bg);
  border-radius: 0.35rem;
  color: var(--report-strong-text);
  display: inline-flex;
  flex-direction: column;
  line-height: 1.25;
  min-width: 70px;
  padding: 0.35rem 0.5rem;
  text-align: right;
}
.share-pill small {
  color: var(--report-share-label-text);
  font-size: 0.66rem;
}
.bill-header {
  align-items: center;
  background: var(--report-detail-bg);
  border-bottom: 1px solid var(--report-border);
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr 1fr auto;
  padding: 1rem 1.25rem;
}
.bill-metrics {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  padding: 1rem 1.25rem;
}
.bill-metric {
  background: var(--report-metric-bg);
  border: 1px solid var(--report-border);
  border-radius: 0.4rem;
  padding: 0.75rem;
}
.min-width-0 {
  min-width: 0;
}

// On desktop, row height is a minimum; wrapped content can still grow. Do not
// impose a height on mobile stacked rows, which contain several label/value pairs.
@media (min-width: 576px) {
  .report-table ::v-deep tbody > tr:not(.b-table-busy-slot) {
    height: var(--report-row-min-height);
  }
}

@media (prefers-reduced-motion: reduce) {
  .report-height-viewport {
    transition: none;
  }
}

@media (max-width: 991.98px) {
  .report-context-header {
    align-items: center;
    flex-wrap: wrap;
  }
  .report-title-wrap {
    flex-basis: auto;
  }
  .report-scope-controls {
    flex-basis: auto;
    justify-content: flex-end;
    margin-left: auto;
    padding-left: 0;
  }
  .report-scope-controls.has-agent-selector {
    flex-basis: 100%;
    justify-content: flex-start;
    margin-left: 0;
    padding-left: 3rem;
  }
  .agent-selector-wrap {
    margin-right: auto;
    width: min(360px, 55vw);
  }
  .report-tabs {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    overflow-x: visible;
  }
  .report-body {
    padding: 1.25rem;
  }
}

@media (max-width: 767.98px) {
  .report-context-header {
    align-items: start;
    display: grid;
    gap: 0.75rem;
    grid-template-columns: 2rem minmax(0, 1fr);
    padding: 1rem;
  }
  .back-button {
    grid-column: 1;
  }
  .report-title-wrap {
    grid-column: 2;
    width: 100%;
  }
  .report-scope-controls {
    align-items: center;
    grid-column: 2;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 0.75rem;
    justify-content: flex-start;
    margin-left: 0;
    padding-left: 0;
    width: 100%;
  }
  .report-scope-controls.has-agent-selector {
    padding-left: 0;
  }
  .agent-selector-wrap {
    flex: 1 1 100%;
    margin-right: 0;
    width: 100%;
  }
  .include-downlines-switch {
    margin-left: 0;
  }
  .include-downlines-control {
    flex: 1 0 100%;
    white-space: normal;
  }
  .report-tab {
    min-height: 58px;
  }
  .report-body {
    padding: 1rem;
  }
  .report-status-row {
    flex-direction: column;
  }
  .report-metadata {
    align-items: flex-start;
  }
  .summary-card {
    align-items: flex-start;
    flex-direction: column;
    gap: 0.5rem;
    min-height: 105px;
  }
  .report-filter-panel {
    padding-left: 0.85rem;
    padding-right: 0.85rem;
  }
  .bill-header {
    align-items: start;
    grid-template-columns: 1fr 1fr;
  }
  .bill-header > :last-child {
    grid-column: 1 / -1;
  }
  .bill-metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 420px) {
  .report-title {
    font-size: 1.15rem;
  }
  .summary-icon {
    height: 36px;
    width: 36px;
  }
  .summary-value {
    font-size: 0.96rem;
  }
  .report-tab {
    font-size: 0.86rem;
    gap: 0.4rem;
    padding: 0.65rem 0.4rem;
  }
  .bill-header,
  .bill-metrics {
    grid-template-columns: 1fr;
  }
  .bill-header > :last-child {
    grid-column: auto;
  }
}
</style>

<style lang="scss">
.lotto-round-report {
  .agent-selector-wrap .vs__search::placeholder,
  .report-filter-panel .form-control::placeholder {
    color: var(--report-secondary-text);
    opacity: 1;
  }
}

.lotto-round-report-bill-modal .bill-items-table {
  --report-label-text: var(--report-strong-text);
}

.dark-layout .lotto-round-report,
.dark-layout .lotto-round-report-bill-modal {
  .summary-value,
  .bill-metric > strong {
    color: var(--hdg-ink, #d0d2d6);
  }

  .text-primary {
    color: var(--report-action-text) !important;
  }

  .tab-icon {
    opacity: 0.85;
  }

  .report-tab:hover .tab-icon,
  .report-tab:focus-visible .tab-icon,
  .report-tab.active .tab-icon {
    opacity: 1;
  }

  .back-button:not(:disabled):not(.disabled),
  .report-rebuild-button:not(:disabled):not(.disabled),
  .btn-outline-secondary:not(:disabled):not(.disabled) {
    color: var(--report-control-text);
  }
}
</style>
